import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { controlFocus, minTarget } from '../primitives/shared.js';

export interface ListRowProps extends HTMLAttributes<HTMLLIElement> {
  /** Names the row action from row content, never from "Open" (07-11 PRM-22). */
  actionLabel?: string;
  href?: string;
  onActivate?: () => void;
  selected?: boolean;
  disabled?: boolean;
  loading?: boolean;
  error?: string;
  trailing?: ReactNode;
  children: ReactNode;
}

/** PRM-22 List row — one record in a vertical list. */
export function ListRow({
  actionLabel,
  href,
  onActivate,
  selected,
  disabled,
  loading,
  error,
  trailing,
  className,
  children,
  ...props
}: ListRowProps) {
  const interactive = Boolean(href || onActivate) && !disabled;

  const content = (
    <>
      <span className="flex min-w-0 flex-1 flex-col gap-xs">{children}</span>
      {trailing ? <span className="shrink-0">{trailing}</span> : null}
    </>
  );

  return (
    <li
      data-primitive="PRM-22"
      aria-disabled={disabled || undefined}
      aria-busy={loading || undefined}
      className={cn(
        'flex list-none items-center gap-inline border-b border-line-subtle p-row-padding',
        interactive && 'hov:bg-sunken',
        selected && 'bg-selected',
        disabled && 'text-disabled',
        loading && 'opacity-70',
        error && 'border-l-2 border-l-line-danger',
        className,
      )}
      {...props}
    >
      {interactive ? (
        <a
          href={href ?? '#'}
          aria-label={actionLabel}
          aria-current={selected ? 'true' : undefined}
          onClick={
            onActivate
              ? (e) => {
                  if (!href) e.preventDefault();
                  onActivate();
                }
              : undefined
          }
          className={cn(
            'flex flex-1 items-center gap-inline text-default no-underline',
            minTarget,
            controlFocus,
            'active:bg-selected',
          )}
        >
          {content}
        </a>
      ) : (
        content
      )}
      {error ? (
        <span role="alert" className="text-body-sm text-danger">
          {error}
        </span>
      ) : null}
      {selected ? <span className="sr-only">Selected</span> : null}
    </li>
  );
}
